import dayjs from 'dayjs'
import { AddressLine } from '../../../../services/als'
import { splitAddress } from '../../../../services/als/splitAddress'
import { AccountAddress } from '../../../azure-b2c/storage'
import { FreightCharge, MonthlyOrder, SaleAgreementPromoProduct, SiteInfo } from './types'

export const getFullAddressForCampaign = (address: AddressLine | AccountAddress) => {
  // this is address line from search address api
  if ('UniqueId' in address) {
    const { first, second } = splitAddress(address.FullAddress)
    return {
      name: first,
      fullAddress: second,
    }
  }

  return {
    name: address.name,
    fullAddress: address.fullAddress,
  }
}

/**
 * Divides a total quantity evenly across the months between a given
 * start and end timestamp, distributing any remainder across the first
 * few months to ensure an even spread.
 *
 * @param total - The total quantity to be divided across the months.
 * @param start - The start timestamp in ISO format (e.g., "2024-01-15T00:00:00Z").
 * @param end - The end timestamp in ISO format (e.g., "2024-06-15T00:00:00Z").
 * @returns An array of MonthlyOrder objects, where each object contains
 * the year, month (1-12), and distributed quantity for that month, based
 * on the range of months between the start and end timestamps.
 *
 * @example
 * divideIntoMonths(62, "2024-01-15T00:00:00Z", "2024-06-15T00:00:00Z");
 * // Returns:
 * // [
 * //   { year: 2024, month: 1, quantity: 11 },
 * //   { year: 2024, month: 2, quantity: 11 },
 * //   { year: 2024, month: 3, quantity: 10 },
 * //   { year: 2024, month: 4, quantity: 10 },
 * //   { year: 2024, month: 5, quantity: 10 },
 * //   { year: 2024, month: 6, quantity: 10 }
 * // ]
 */
export const divideIntoMonths = (total: number, start: string, end: string): MonthlyOrder[] => {
  const startDate = dayjs(start)
  const endDate = dayjs(end)

  const monthsBetween: { year: number; month: number }[] = []
  const totalMonths = endDate.diff(startDate, 'month')

  for (let i = 0; i <= totalMonths; i++) {
    const current = startDate.add(i, 'month')
    monthsBetween.push({
      year: current.year(),
      month: current.month(), // JavaScript months are 0-based
    })
  }

  const baseAmount = Math.floor(total / monthsBetween.length)
  const remainder = total % monthsBetween.length

  return monthsBetween.map((entry, index) => ({
    year: entry.year,
    month: entry.month,
    quantity: baseAmount + (index < remainder ? 1 : 0),
  }))
}

export const unitMap = {
  tonne: 't',
  t: 't',
  each: 'unit',
  ea: 'unit',
  slg: 'sl',
  sling: 'sl',
  sl: 'sl',
  kg: 'kg',
  kilo: 'kg',
}

export const unitMapLong = {
  tonne: 'tonne',
  t: 'tonne',
  each: 'unit',
  ea: 'unit',
  slg: 'sling',
  sling: 'sling',
  sl: 'sling',
  kg: 'kg',
  kilo: 'kg',
}

export const unitMapPlural = {
  tonne: 'Tonnes',
  t: 'Tonnes',
  each: 'Units',
  ea: 'Units',
  slg: 'Slings',
  sling: 'Slings',
  sl: 'Slings',
  kg: 'Kg',
  kilo: 'Kg',
}

export const calculateTotalFreightCost = (
  productSelected: SaleAgreementPromoProduct | SaleAgreementPromoProduct[],
  freightCharge: FreightCharge,
): number => {
  // Return 0 if freightCharge is null, undefined, or 0
  if (!freightCharge) {
    return 0
  }

  let totalQuantity = 0

  if (Array.isArray(productSelected)) {
    // Calculate the total orders quantity across all products
    totalQuantity = productSelected.reduce(
      (sum, product) =>
        sum + (product.orders?.reduce((sum2, order) => sum2 + order.quantity, 0) ?? 0),
      0,
    )
  } else {
    totalQuantity = productSelected.orders?.reduce((sum2, order) => sum2 + order.quantity, 0) ?? 0
  }

  // Return the total freight cost
  return freightCharge * totalQuantity
}

export const getPrice = (product: SaleAgreementPromoProduct) => {
  return product.editedPrice ?? product.unitPrice
}

export const getSites = (products: SaleAgreementPromoProduct[]) => {
  const sitesObj = products.reduce<Record<string, SiteInfo>>((carry, product) => {
    carry[product.siteId] = {
      siteId: product.siteId,
      siteName: product.siteName,
      sitePostcode: product.sitePostcode,
    }
    return carry
  }, {})
  return Object.values(sitesObj).sort((a, b) => a.siteName.localeCompare(b.siteName))
}

export const sortCampaignProducts = (products: SaleAgreementPromoProduct[]) => {
  return products.toSorted((a, b) => a.description.localeCompare(b.description))
}

export const isLocationInSameIsland = (sitePostcode: number, addressPostcode: number) => {
  const bothInSouthIsland = sitePostcode >= 7000 && addressPostcode >= 7000
  const bothInNorthIsland = sitePostcode < 7000 && addressPostcode < 7000
  return bothInSouthIsland || bothInNorthIsland
}
